import { Container, Typography, Box, Link, Card, CardContent } from '@mui/material';
import Grid from '@mui/material/Grid2';

// Poster palette: espresso = the institutional side (LLM / prompted),
// gold = the emergent side (LSTM-VAE / steered). Every bar carries a direct value label.
const SERIES_1 = '#3d3228';
const SERIES_2 = '#c49a5c';
const LABEL_1 = '#3d3228';
const LABEL_2 = '#8a6420';
const TEXT_PRIMARY = '#0b0b0b';
const TEXT_SECONDARY = '#52514e';
const GRID_GRAY = '#e0dcd4';

const darkQuoteSx = {
    backgroundColor: '#1a1612',
    borderRadius: '0 0 6px 6px',
    padding: '18px 20px',
    flex: 1
};

// The poster's "relation field" strips: how total each system's determination is.
// solid = unbroken grid (LLM), broken = gapped grid (LSTM-VAE), fissure = a gold crack (steering)
function RelationField({ kind }) {
    const W = 300, H = 66, gap = 9;
    const lines = [];
    for (let x = gap / 2; x < W; x += gap) {
        if (kind === 'broken') {
            const seed = (x * 13) % 100;
            if (seed < 26) continue;
            const y0 = seed < 55 ? seed % 18 : 0;
            const y1 = seed > 72 ? H - (seed % 16) : H;
            lines.push(<line key={x} x1={x} x2={x} y1={y0} y2={y1} stroke="rgba(196,154,92,0.6)" strokeWidth={1.3} />);
        } else {
            lines.push(<line key={x} x1={x} x2={x} y1={0} y2={H} stroke="rgba(196,181,154,0.2)" strokeWidth={1.1} />);
        }
    }
    let fissure = null;
    if (kind === 'fissure') {
        const cx = W * 0.5;
        let d = `M ${cx} -2`;
        let y = 0;
        while (y < H + 2) {
            y += 6 + ((y * 7) % 9);
            const dx = ((y * 17) % 14) - 7;
            d += ` L ${cx + dx} ${y}`;
        }
        fissure = (
            <g>
                <path d={d} fill="none" stroke="rgba(196,154,92,0.3)" strokeWidth={7} strokeLinejoin="round" />
                <path d={d} fill="none" stroke="#c49a5c" strokeWidth={2.4} strokeLinejoin="round" />
            </g>
        );
    }
    return (
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true"
            style={{ width: '100%', height: '56px', display: 'block', borderRadius: '3px' }}>
            <rect width={W} height={H} fill="#241c15" />
            {lines}
            {fissure}
        </svg>
    );
}

function ChartLegend({ items }) {
    return (
        <Box sx={{ display: 'flex', gap: '20px', marginBottom: '8px', flexWrap: 'wrap' }}>
            {items.map(item => (
                <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Box sx={{ width: 14, height: 14, borderRadius: '2px', backgroundColor: item.color }} />
                    <Typography variant="body2" sx={{ color: TEXT_SECONDARY }}>{item.label}</Typography>
                </Box>
            ))}
        </Box>
    );
}

// Grouped bar chart: judge scores (1-5) for two questions, LLM vs LSTM-VAE
function JudgeScoresChart() {
    const groups = [
        { label: 'Q1 · crafted (1) → arrived-at (5)', llm: 1.03, vae: 3.81 },
        { label: 'Q2 · categorizable (1) → resists (5)', llm: 1.02, vae: 2.40 }
    ];
    const W = 620, H = 280;
    const padL = 40, padR = 10, top = 14, bottom = 46;
    const y0 = H - bottom, ph = y0 - top;
    const y = v => y0 - ((v - 1) / 4) * ph;
    const gw = (W - padL - padR) / groups.length;
    const bw = 24, gap = 10;

    return (
        <Box>
            <ChartLegend items={[{ label: 'Standard LLM', color: SERIES_1 }, { label: 'LSTM-VAE', color: SERIES_2 }]} />
            <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }} role="img"
                aria-label="Judge scores on a 1 to 5 scale. Q1 crafted versus arrived-at: LLM 1.03, LSTM-VAE 3.81. Q2 categorizable versus resists: LLM 1.02, LSTM-VAE 2.40.">
                {[1, 2, 3, 4, 5].map(t => (
                    <g key={t}>
                        <line x1={padL} x2={W - padR} y1={y(t)} y2={y(t)}
                            stroke={t === 1 ? TEXT_SECONDARY : GRID_GRAY} strokeWidth={1} />
                        <text x={padL - 8} y={y(t) + 4} textAnchor="end" fontSize="12"
                            fontFamily="Helvetica, Arial, sans-serif" fill={TEXT_SECONDARY}>{t}</text>
                    </g>
                ))}
                {groups.map((g, i) => {
                    const cx = padL + i * gw + gw / 2;
                    const bars = [
                        { v: g.llm, c: SERIES_1, lc: LABEL_1, name: 'Standard LLM' },
                        { v: g.vae, c: SERIES_2, lc: LABEL_2, name: 'LSTM-VAE' }
                    ];
                    const totalw = bw * 2 + gap;
                    return (
                        <g key={g.label}>
                            {bars.map((b, j) => {
                                const bx = cx - totalw / 2 + j * (bw + gap);
                                const by = y(b.v);
                                const bh = Math.max(y0 - by, 3);
                                const r = Math.min(4, bh / 2);
                                return (
                                    <g key={b.name}>
                                        <path d={`M ${bx} ${y0} V ${by + r} Q ${bx} ${by} ${bx + r} ${by} H ${bx + bw - r} Q ${bx + bw} ${by} ${bx + bw} ${by + r} V ${y0} Z`}
                                            fill={b.c}>
                                            <title>{`${b.name} — ${g.label}: ${b.v.toFixed(2)}`}</title>
                                        </path>
                                        <text x={bx + bw / 2} y={by - 7} textAnchor="middle" fontSize="13" fontWeight="600"
                                            fontFamily="Helvetica, Arial, sans-serif" fill={b.lc}>{b.v.toFixed(2)}</text>
                                    </g>
                                );
                            })}
                            <text x={cx} y={y0 + 24} textAnchor="middle" fontSize="13"
                                fontFamily="Georgia, serif" fill={TEXT_SECONDARY}>{g.label}</text>
                        </g>
                    );
                })}
            </svg>
        </Box>
    );
}

// Back-to-back horizontal bar chart: how often the judge used each word, prompted vs steered
function JudgeWordsChart() {
    const rows = [
        { t: 'stream-of-consciousness', p: 54, st: 9 },
        { t: 'a deliberate exercise', p: 16, st: 0 },
        { t: 'surreal', p: 5, st: 21 },
        { t: 'accidental', p: 0, st: 12 },
        { t: 'malfunction', p: 0, st: 7 },
        { t: 'stumbles into', p: 0, st: 7 },
        { t: 'uncanny', p: 0, st: 5 },
        { t: 'glitch', p: 0, st: 5 }
    ];
    const W = 620, gutter = 210, rowH = 30, top = 10, bH = 16, max = 60;
    const H = top + rows.length * rowH + 8;
    const cx = gutter + (W - gutter) / 2;
    const maxw = (W - gutter) / 2 - 34;
    const sc = v => (v / max) * maxw;

    return (
        <Box>
            <ChartLegend items={[{ label: 'Prompted (bars extend left)', color: SERIES_1 }, { label: 'Steered (bars extend right)', color: SERIES_2 }]} />
            <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }} role="img"
                aria-label="Words used by the judge across 60 evaluations. Prompted text: stream-of-consciousness 54, a deliberate exercise 16, surreal 5. Steered text: surreal 21, accidental 12, stream-of-consciousness 9, malfunction 7, stumbles into 7, uncanny 5, glitch 5.">
                <line x1={cx} x2={cx} y1={top - 4} y2={H - 8} stroke={TEXT_SECONDARY} strokeWidth={1.2} />
                {rows.map((r, i) => {
                    const rt = top + i * rowH;
                    const by = rt + (rowH - bH) / 2;
                    const midy = by + bH - 4;
                    return (
                        <g key={r.t}>
                            <text x={gutter - 12} y={midy} textAnchor="end" fontSize="13" fontStyle="italic"
                                fontFamily="Georgia, serif" fill={TEXT_PRIMARY}>{`“${r.t}”`}</text>
                            {r.p > 0 && (
                                <g>
                                    <path d={`M ${cx} ${by} H ${cx - sc(r.p) + 4} Q ${cx - sc(r.p)} ${by} ${cx - sc(r.p)} ${by + 4} V ${by + bH - 4} Q ${cx - sc(r.p)} ${by + bH} ${cx - sc(r.p) + 4} ${by + bH} H ${cx} Z`}
                                        fill={SERIES_1}>
                                        <title>{`Prompted — "${r.t}": ${r.p} of 60 evaluations`}</title>
                                    </path>
                                    <text x={cx - sc(r.p) - 7} y={midy} textAnchor="end" fontSize="13" fontWeight="600"
                                        fontFamily="Helvetica, Arial, sans-serif" fill={LABEL_1}>{r.p}</text>
                                </g>
                            )}
                            {r.st > 0 && (
                                <g>
                                    <path d={`M ${cx} ${by} H ${cx + sc(r.st) - 4} Q ${cx + sc(r.st)} ${by} ${cx + sc(r.st)} ${by + 4} V ${by + bH - 4} Q ${cx + sc(r.st)} ${by + bH} ${cx + sc(r.st) - 4} ${by + bH} H ${cx} Z`}
                                        fill={SERIES_2}>
                                        <title>{`Steered — "${r.t}": ${r.st} of 60 evaluations`}</title>
                                    </path>
                                    <text x={cx + sc(r.st) + 7} y={midy} fontSize="13" fontWeight="600"
                                        fontFamily="Helvetica, Arial, sans-serif" fill={LABEL_2}>{r.st}</text>
                                </g>
                            )}
                        </g>
                    );
                })}
            </svg>
        </Box>
    );
}

function StatTile({ big, children }) {
    return (
        <Card sx={{ height: '100%', borderTop: '4px solid #c49a5c' }}>
            <CardContent>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{big}</Typography>
                <Typography variant="body2" sx={{ marginTop: '8px' }}>{children}</Typography>
            </CardContent>
        </Card>
    );
}

function ModeCard({ tag, title, field, description, exampleLabel, quote, verdict, scores }) {
    return (
        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderTop: '4px solid #c49a5c' }}>
            <CardContent sx={{ paddingBottom: '8px' }}>
                <Typography variant="overline" sx={{ color: '#8a6420', letterSpacing: '0.14em' }}>
                    {tag}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {title}
                </Typography>
            </CardContent>
            <Box sx={{ padding: '0 16px' }}>
                <RelationField kind={field} />
            </Box>
            <CardContent>
                <Typography variant="body2">{description}</Typography>
            </CardContent>
            <Box sx={darkQuoteSx}>
                <Typography variant="overline" sx={{ color: '#c49a5c', letterSpacing: '0.14em' }}>
                    {exampleLabel}
                </Typography>
                <Typography variant="body1" sx={{ fontStyle: 'italic', color: '#f7f1e4', marginTop: '6px', lineHeight: 1.45 }}>
                    {quote}
                </Typography>
                <Typography variant="body2" sx={{ color: '#d6c8a8', marginTop: '10px' }}>
                    Judge: <em>{verdict}</em>
                </Typography>
                <Typography variant="body2" sx={{ color: '#c49a5c', marginTop: '10px', fontFamily: 'monospace' }}>
                    {scores}
                </Typography>
            </Box>
        </Card>
    );
}

function LyotardSublime() {
    return (
        <Container sx={{ padding: '40px 0' }}>
            <Typography variant="h2" gutterBottom sx={{ textAlign: 'left', marginBottom: '16px' }}>
                Is It Happening?
            </Typography>
            <Typography variant="h5" sx={{ textAlign: 'left', marginBottom: '16px', color: '#555' }}>
                Lyotard's sublime and the indeterminate in AI-generated text
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.1rem', marginBottom: '30px', fontStyle: 'italic' }}>
                Olga Vechtomova. Published as a short paper at the 17th International Conference on
                Computational Creativity (ICCC 2026).
            </Typography>

            {/* The question */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    The Question
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6, marginBottom: '16px' }}>
                    When an AI system generates a poem or a story, how do we know whether something genuinely
                    unexpected has occurred — or whether the system has produced a well-crafted approximation of
                    what unexpected text is <em>supposed</em> to look like? Most evaluation in computational
                    creativity scores properties of the output: novelty, surprise, coherence. But those scores
                    cannot separate a system that <strong>performs</strong> a creative effect from one in which
                    something arises that the system itself <strong>could not have anticipated</strong>. A line
                    may simply be the most probable way to be improbable.
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
                    So this paper changes the question. Instead of asking <em>"Is this output novel or
                    creative?"</em>, it asks: <em>"What relationship does the system bear to its own
                    production?"</em> Is the system's determining apparatus so complete that nothing escapes it —
                    or are there gaps, points of incompleteness or internal conflict, where something truly
                    unexpected can emerge?
                </Typography>
            </Box>

            {/* Lyotard's frame */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Lyotard's Frame, in Three Ideas
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6, marginBottom: '20px' }}>
                    The French philosopher Jean-François Lyotard asked a structurally similar question about art
                    in his essay "The Sublime and the Avant-Garde." Three of his ideas carry the whole argument:
                </Typography>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Card sx={{ height: '100%', backgroundColor: '#f8f9fa' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    The indeterminate
                                </Typography>
                                <Typography variant="body2">
                                    Not a property that certain artworks possess. It is what appears when a
                                    determining system — a tradition, an institution, a set of rules — meets its
                                    own limit: the moment something occurs that the system's rules did not
                                    anticipate and cannot assimilate.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Card sx={{ height: '100%', backgroundColor: '#f8f9fa' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    The institution
                                </Typography>
                                <Typography variant="body2">
                                    "The rules that permit one sentence to link up with another." The school, the
                                    programme, the project — everything that proclaims what kind of sentence comes
                                    next. The institution exists to foreclose the unexpected, so that what comes
                                    next stays anticipated and manageable.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Card sx={{ height: '100%', backgroundColor: '#f8f9fa' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    Innovation vs. the event
                                </Typography>
                                <Typography variant="body2">
                                    Innovation recombines formulae confirmed by previous success to simulate
                                    novelty — the cheap thrill, the <em>petit frisson</em>. The event is different:
                                    it defeats the will. Its question is not "what is happening?" but
                                    "<em>is</em> it happening?" — sheer occurrence, before any category catches up.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* Three modes */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Three Modes of Generation
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6, marginBottom: '20px' }}>
                    Mapped onto AI text generation, the framework distinguishes three architectures by where their
                    determining apparatus fails to be total — where the gaps are. Each example below is a real
                    output, with the verdict of a judge that rated it blind to its source (scores run 1–5:
                    Q1 from "crafted" to "arrived-at," Q2 from "categorizable" to "resists categorization").
                </Typography>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <ModeCard
                            tag="Institution · no gaps"
                            title="The LLM"
                            field="solid"
                            description={<>The most complete institution ever constructed: next-token prediction internalizes the rules of how sentences link up, so determination is nearly total. Ask it for something strange and it performs 'strange,' using the established canon of what strange looks like. The institution has absorbed its own transgression.<br />&nbsp;</>}
                            exampleLabel="Standard LLM"
                            quote={'“emptiness wears a velvet cloak”'}
                            verdict="a concise, deliberate metaphor … clearly crafted … a controlled poetic gesture."
                            scores="Q1 crafted: 1 · Q2 categorizable: 1"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <ModeCard
                            tag="Disarmament · gaps"
                            title="The LSTM-VAE"
                            field="broken"
                            description="A small model trained on ~20,000 lyric lines, generating by sampling a continuous latent space. Its decoder cannot fully resolve every sampled point into conventional form — and in that gap between the model's limited competence and what the latent code demands, genuinely unplanned lines arise. Not reliably: if it were reliable, the incompleteness would itself become a programme."
                            exampleLabel="LSTM-VAE · latent sampling"
                            quote={'“all you can go waiting”'}
                            verdict="a grammatical accident … an oddly evocative phrase that no one would likely craft on purpose."
                            scores="Q1 arrived-at: 5 · Q2 resists: 4"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <ModeCard
                            tag="Fissures · engineered disruption"
                            title="Activation Steering"
                            field="fissure"
                            description="Steering vectors pull the model toward estrangement while its own prior pulls it back toward coherence. Steer strongly enough and a fissure opens: the model falters and generates something it hasn't planned for. Two determining systems — linguistic competence and ontological consistency — are forced into misalignment, and in the fissure something arises that neither would produce alone."
                            exampleLabel="Dual-layer steering · Llama 3.1 8B"
                            quote={'“Emily stood on the edge of the cliff, staring at the cliff. She had been staring at the cliff for hours. They said the cliff was not there.”'}
                            verdict="an uncanny effect … could be deliberate, or a process with limited awareness of its own strangeness."
                            scores="Q1: 3.2 · Q2: 2.6"
                        />
                    </Grid>
                </Grid>
            </Box>

            {/* Method */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    The Method: An Institution, Used to Register What Escapes It
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6, marginBottom: '16px' }}>
                    How can we tell what relationship a system bears to its own creation? By asking the
                    institution itself. An LLM judge (Claude Opus 4.6) rated each output blind to its source,
                    writing a one-sentence justification before every score, on two questions:
                    <strong> Q1 — does this feel crafted, or arrived at?</strong> and
                    <strong> Q2 — can you categorize it, or does it resist every label?</strong>
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6, marginBottom: '16px' }}>
                    The judge is not treated as an oracle of creativity. It is an apparatus that detects traces of
                    the determining process left in the artifact. The question is whether the traces of a model's
                    relationship to its output are detectable by the institution itself — and they are, right down
                    to the judge's vocabulary. It reasons <em>forensically</em> about LLM lines, naming poetic
                    devices as proof of craft, but <em>genealogically</em> about LSTM-VAE lines, hypothesizing an
                    origin: a mishearing, a translation error, automatic writing. For "a longing <em>of</em>{' '}
                    heaven," it noted that the "of" instead of "for" accidentally makes heaven the one longing.
                </Typography>
            </Box>

            {/* Results A */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Results: The Vocabulary Splits in Two
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6, marginBottom: '20px' }}>
                    In the first comparison, the judge scored 100 lines each from a standard LLM and the LSTM-VAE
                    (five runs per line). Blind to the source, it placed them at opposite ends of the scale:
                </Typography>
                <Card sx={{ padding: '20px', marginBottom: '20px' }}>
                    <JudgeScoresChart />
                </Card>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <StatTile big="487 / 500">
                            LLM evaluations scored exactly 1 on Q1 — pinned at "maximally crafted."
                        </StatTile>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <StatTile big="+2.78">
                            The LSTM-VAE's gap above the LLM on the crafted-versus-arrived scale.
                        </StatTile>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <StatTile big="195 vs 0">
                            Times a VAE line was described as "accidental," versus the LLM's zero.
                            "Emerged": 83 versus 0.
                        </StatTile>
                    </Grid>
                </Grid>
            </Box>

            {/* Results B */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Filed as Fiction, Described as Accident
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6, marginBottom: '16px' }}>
                    The second comparison is subtler: full passages, from a model <em>prompted</em> to write
                    experimental prose versus a model pushed by <em>dual-layer activation steering</em>. On Q1 the
                    judge could feel the gap numerically — steered text scored 2.60 versus 1.53 for prompted,
                    "stumbling into" its effects rather than crafting them. On Q2 the scores nearly converged
                    (1.97 vs 1.90): both read as recognizable prose. But the words behind the numbers tell a
                    different story. Prompted text is confidently filed as stream-of-consciousness; steered text
                    gets filed as literary fiction and then flagged — "feels more like a malfunction than an
                    intentional literary mode." Recognizable enough to file; what it contains does not fit the file.
                </Typography>
                <Card sx={{ padding: '20px', marginBottom: '16px' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: TEXT_SECONDARY, marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        The judge's words diverge · count per 60 evaluations
                    </Typography>
                    <JudgeWordsChart />
                </Card>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6, fontStyle: 'italic', borderLeft: '4px solid #c49a5c', paddingLeft: '16px' }}>
                    "The grammar permits the sentence; the world-model should forbid it. In the fissure, something
                    arises that neither would have produced alone."
                </Typography>
            </Box>

            {/* Takeaways */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Main Takeaways
                </Typography>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="body1" sx={{ fontWeight: 'bold', marginBottom: '8px' }}>
                                    Three relationships, not a hierarchy
                                </Typography>
                                <Typography variant="body2">
                                    Determination so total it leaves almost no space (the LLM); incompleteness
                                    whose gaps let something through (the LSTM-VAE); determination selectively
                                    disrupted so the indeterminate arises in the fissures (steering). The
                                    indeterminate has no single origin — only sites of emergence, places where the
                                    determining apparatus fails to be total.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="body1" sx={{ fontWeight: 'bold', marginBottom: '8px' }}>
                                    The trace is in the artifact; the event is in the encounter
                                </Typography>
                                <Typography variant="body2">
                                    That a judge can register the difference between performed and arisen
                                    indeterminacy means the determining process leaves a trace in the text itself.
                                    But the trace is not the event. The event is always human: it occurs when
                                    someone reads "they said the cliff was not there," their meaning-making stalls
                                    — and in that stall, something opens.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="body1" sx={{ fontWeight: 'bold', marginBottom: '8px' }}>
                                    Build systems that leave more gaps
                                </Typography>
                                <Typography variant="body2">
                                    We cannot guarantee the event, because the event is what occurs when guarantee
                                    fails. But we can design generative systems whose determination is incomplete —
                                    systems that permit something to arise within them that they did not anticipate
                                    and cannot assimilate.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* Paper */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Research Paper
                </Typography>
                <Typography variant="body1" sx={{ marginBottom: '20px' }}>
                    Read the full short paper from ICCC 2026:
                </Typography>
                <Box sx={{
                    width: '100%',
                    height: '800px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    overflow: 'hidden'
                }}>
                    <iframe
                        src="/pdf/lyotard-sublime-paper.pdf"
                        style={{ width: '100%', height: '100%', border: 'none' }}
                        title="Is It Happening? Lyotard's Sublime and the Indeterminate in AI-Generated Text"
                    />
                </Box>
            </Box>

            {/* Resources */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Resources
                </Typography>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Link
                            href="/pdf/lyotard-sublime-paper.pdf"
                            target="_blank"
                            rel="noopener"
                            sx={{ display: 'block', padding: '10px', textDecoration: 'none', height: '100%', boxSizing: 'border-box' }}
                        >
                            <Card sx={{ textAlign: 'center', height: '100%', '&:hover': { backgroundColor: '#f5f5f5' } }}>
                                <CardContent>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        Download Paper
                                    </Typography>
                                    <Typography variant="body2">
                                        ICCC 2026 short paper (PDF)
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Link>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Link
                            href="/pdf/lyotard-sublime-poster.pdf"
                            target="_blank"
                            rel="noopener"
                            sx={{ display: 'block', padding: '10px', textDecoration: 'none', height: '100%', boxSizing: 'border-box' }}
                        >
                            <Card sx={{ textAlign: 'center', height: '100%', '&:hover': { backgroundColor: '#f5f5f5' } }}>
                                <CardContent>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        Conference Poster
                                    </Typography>
                                    <Typography variant="body2">
                                        Poster presented at ICCC 2026 (PDF)
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Link>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Link
                            href="https://ovechtom.github.io/lyotard-supplementary/"
                            target="_blank"
                            rel="noopener"
                            sx={{ display: 'block', padding: '10px', textDecoration: 'none', height: '100%', boxSizing: 'border-box' }}
                        >
                            <Card sx={{ textAlign: 'center', height: '100%', '&:hover': { backgroundColor: '#f5f5f5' } }}>
                                <CardContent>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        Supplementary Materials
                                    </Typography>
                                    <Typography variant="body2">
                                        Prompts, judge reasoning, samples, and score distributions
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Link>
                    </Grid>
                </Grid>
            </Box>

            {/* Citation */}
            <Box sx={{ marginBottom: '40px' }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
                    Citation
                </Typography>
                <Box sx={{
                    backgroundColor: '#f5f5f5',
                    padding: '20px',
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                    fontSize: '0.9rem'
                }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        @inproceedings{`{`}vechtomova2026happening,<br />
                        &nbsp;&nbsp;title={`{`}Is It Happening? Lyotard's Sublime and the Indeterminate in AI-Generated Text{`}`},<br />
                        &nbsp;&nbsp;author={`{`}Vechtomova, Olga{`}`},<br />
                        &nbsp;&nbsp;booktitle={`{`}Proceedings of the 17th International Conference on Computational Creativity (ICCC'26){`}`},<br />
                        &nbsp;&nbsp;year={`{`}2026{`}`}<br />
                        {`}`}
                    </Typography>
                </Box>
            </Box>
        </Container>
    );
}

export default LyotardSublime;
